import React, { useState } from "react";
import { FormControl } from "react-bootstrap";
import { toast } from "react-toastify";
import { useIntl } from "react-intl";

import Button from "components/atoms/Button/Button";
import { FilterPreset, FilterPresetType } from "types/Common";
import errorToast from "helpers/errorToast";
import { updateFilterPresets } from "api";
import { useGetFilterPresets } from "api/queries/user/useGetFilterPresets";

type CreatePresetProps = {
  presetType: FilterPresetType;
  currentFilters: Record<string, any>;
  setIsSaveMode: React.Dispatch<React.SetStateAction<boolean>>;
  presets: FilterPreset[];
  setSelectedPreset: React.Dispatch<React.SetStateAction<string>>;
  eventContext: string; // eslint-disable-line react/no-unused-prop-types
};

function CreatePreset(props: CreatePresetProps) {
  const intl = useIntl();
  const { setIsSaveMode, presetType, currentFilters, presets, setSelectedPreset } = props;
  const [label, setLabel] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const { refetch } = useGetFilterPresets({
    refetchOnMount: false,
  });

  const handleSaveClick = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    e.stopPropagation();
    if (!label?.trim()?.length) {
      return;
    }
    if (presets.find((item) => item.label.toLowerCase() === label.toLowerCase())) {
      errorToast(null, "Preset with same name already exist");
      return;
    }
    setIsSaving(true);
    const newPreset: FilterPreset = {
      label,
      type: presetType,
      presets: Object.keys(currentFilters)
        .filter(
          (key) =>
            (Array.isArray(currentFilters[key]) && currentFilters[key].length) ||
            (!Array.isArray(currentFilters[key]) && currentFilters[key] && key !== "query"),
        )
        .map((key) => ({
          label: key,
          value: currentFilters[key],
        })),
    };
    const presetsToSave = [...presets, newPreset];
    updateFilterPresets(presetsToSave)
      .then(() => {
        refetch();
        setSelectedPreset(newPreset.label);
        setIsSaveMode(false);
        toast(
          intl.formatMessage({
            id: "general.presetSaved",
            defaultMessage: "Preset saved",
          }),
          { type: "success" },
        );
      })
      .catch((err) => {
        errorToast(err, "Failed to create the preset");
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  return (
    <div>
      <p className="mb-3 font-weight-bold font-b1">Save preset</p>
      <div className="text-filter mb-3">
        <p className="text-primary mb-2 font-b3">Name</p>
        <FormControl
          className="filter-input"
          type="text"
          placeholder={
            intl.formatMessage({
              id: "general.typeHere",
              defaultMessage: "Type here...",
            }) as string
          }
          value={label}
          onChange={(e) => {
            setLabel(e.target.value);
          }}
        />
      </div>
      <div className="d-flex align-items-center">
        <Button
          variant="outline-primary"
          onClick={(e) => {
            e.stopPropagation();
            setIsSaveMode(false);
          }}
        >
          Cancel
        </Button>
        <Button className="ml-auto" onClick={handleSaveClick}>
          Save
          {isSaving && <div className="spinner-border spinner-border-sm mx-2" role="status" />}
        </Button>
      </div>
    </div>
  );
}

export default CreatePreset;
